import { Alert, Button, Form, Input, Modal, Select, Space } from 'antd';
import { ReactNode, useState } from 'react';

import {
    NurseShiftCancellationReason,
    ProfileDto,
    ShiftListItemDto,
    ShiftType,
} from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';

import { useSelection } from '../../../hooks/use-selection';
import { useToast } from '../../../hooks/use-toast';

import { AssignNurse } from './assign-nurse';

const cancellationReasonOptions = Object.values(
    NurseShiftCancellationReason
).map((reason) => ({
    value: reason,
    label: reason.replace(/_/g, ' '),
}));

function CancelAssignmentButton() {
    const api = useApiClient();
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const { notification } = useToast();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    if (new Date(selection.endTime) < new Date()) {
        return null;
    }

    const handleConfirm = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            await api?.shiftControllerCancelShiftAssignment({
                shiftId: selection.id,
                nurseShiftCancellationCreateDto: {
                    reason: values.reason,
                    comment: values.comment || undefined,
                },
            });

            notification.success({
                message: 'Assignment canceled',
                duration: 3,
            });

            setSelection({
                ...selection,
                nurseProfile: null,
            });
            setOpen(false);
            form.resetFields();
        } catch (err) {
            notification.error({
                message: unwrapError(err),
                duration: 3,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button size="small" danger onClick={() => setOpen(true)}>
                Cancel
            </Button>
            <Modal
                title="Cancel Nurse Assignment"
                open={open}
                onCancel={() => setOpen(false)}
                footer={[
                    <Button key="back" onClick={() => setOpen(false)}>
                        Close
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger
                        loading={loading}
                        onClick={() => void handleConfirm()}
                    >
                        Confirm Cancellation
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="reason"
                        label="Cancellation Reason"
                        rules={[
                            {
                                required: true,
                                message: 'Please select a reason',
                            },
                        ]}
                    >
                        <Select
                            placeholder="Select a reason"
                            options={cancellationReasonOptions}
                        />
                    </Form.Item>
                    <Form.Item name="comment" label="Comment (optional)">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
                <Alert
                    message="Email notifications will not be sent for this cancellation."
                    type="info"
                    showIcon
                />
            </Modal>
        </>
    );
}

function NurseProfileField({
    nurseProfile,
    shiftType,
    isEditing,
    onEdit,
    onClose,
}: {
    nurseProfile: ProfileDto | null;
    shiftType: ShiftType;
    isEditing: boolean;
    onEdit: () => void;
    onClose: () => void;
}) {
    if (nurseProfile) {
        return (
            <Space>
                <span>
                    {nurseProfile.firstName} {nurseProfile.lastName}
                </span>
                <CancelAssignmentButton />
            </Space>
        );
    }

    if (shiftType === ShiftType.Orientation) {
        return null;
    }

    if (isEditing) {
        return <AssignNurse close={onClose} />;
    }

    return <a onClick={onEdit}>Assign Nurse</a>;
}

export const createNurseProfileFieldRenderer = (
    shiftType: ShiftType,
    isEditing: boolean,
    onEdit: () => void,
    onClose: () => void
): ((nurseProfile: ProfileDto) => ReactNode) => {
    const renderNurseProfile = (nurseProfile: ProfileDto) => (
        <NurseProfileField
            nurseProfile={nurseProfile}
            shiftType={shiftType}
            isEditing={isEditing}
            onEdit={onEdit}
            onClose={onClose}
        />
    );

    renderNurseProfile.displayName = 'NurseProfileFieldRenderer';
    return renderNurseProfile;
};
